import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LitPage } from '../components/lit-page';
import { navigate } from '../router.ts';
import { _delete, get } from '../utils/http.ts';
import { getCurrentUser } from '../auth/session.ts';
import { Activity, Programme } from '../types.ts';
import initialisePuzzleMetadata from '../puzzles/puzzleMetadata.ts';
import { encodePresetOptions } from '../puzzles/preset.ts';

@customElement('app-programmes')
export class ProgrammesPage extends LitPage {
  @state() puzzles: Map<string, { name: string; id: number }[]> = new Map();

  @state() private programmes: Programme[] = [];
  @state() private loading = true;
  @state() private deletingEmail: string | null = null;

  @state() private puzzlePreviewUrl: string | null = null;

  styles() {
    return css`
      :host {
        display: block;
        min-height: 100vh;
        padding-bottom: 4rem;
      }
  
      .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.75rem;
      }
  
      .page-header-actions {
        display: flex;
        gap: 0.5rem;
      }
  
      .programmes-table-wrapper {
        display: none;
      }
  
      @media (min-width: 768px) {
        .programmes-table-wrapper {
          display: block;
        }
      }
  
      .activities-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.25rem;
      }

      .activities-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.25rem;
      }

      .activities-list li {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.125rem;
      }

      .activities-pill {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.125rem 0.5rem;
        border-radius: 999px;
        background: #f2f2f2;
        font-size: 0.875rem;
        cursor: pointer;
      }

      .activities-arrow {
        font-size: 0.875rem;
        color: #999;
        margin-left: 0;
      }
  
      .programmes-cards {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
  
      @media (min-width: 768px) {
        .programmes-cards {
          display: none;
        }
      }
  
      .programme-card {
        border-radius: 4px;
      }
  
      .programme-card-header {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        margin-bottom: 0.5rem;
      }
  
      .programme-card-email {
        word-break: break-all;
        font-size: 0.875rem;
        color: #666;
      }
  
      .programme-card-activities summary {
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        list-style: none;
      }
  
      .programme-card-activities summary::-webkit-details-marker {
        display: none;
      }
  
      .programme-card-activities summary span {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
      }
  
      .programme-card-activities-count {
        font-size: 0.875rem;
        color: #666;
      }

      .programme-card-footer {
        margin-top: 0.75rem;
        display: flex;
        justify-content: flex-end;
      }

      .programme-row-leave {
        animation: programme-row-leave 200ms ease-in forwards;
      }

      @keyframes programme-row-leave {
        to {
          opacity: 0;
          transform: translateX(-4px);
        }
      }

      .skeleton {
        position: relative;
        overflow: hidden;
        background: #eee;
        border-radius: 4px;
      }

      .skeleton::after {
        content: '';
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background-image: linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) 0,
          rgba(255, 255, 255, 0.6) 50%,
          rgba(255, 255, 255, 0) 100%
        );
        animation: skeleton-loading 1.2s infinite;
      }

      @keyframes skeleton-loading {
        100% {
          transform: translateX(100%);
        }
      }

      .skeleton-text {
        height: 0.9rem;
        margin-bottom: 0.4rem;
      }

      .skeleton-text.wide {
        width: 80%;
      }

      .skeleton-text.mid {
        width: 60%;
      }

      .skeleton-text.short {
        width: 40%;
      }

      .skeleton-pill {
        width: 120px;
        height: 1.5rem;
        border-radius: 999px;
      }

      /* Minimal overlay for the puzzle preview (replaces UIkit modal chrome — legacy page, U010 owns reskin) */
      #puzzle-preview-modal {
        position: fixed;
        inset: 0;
        z-index: 100;
        background: rgba(0, 0, 0, 0.6);
      }

      #puzzle-preview-modal[hidden] {
        display: none;
      }

      .puzzle-preview-dialog {
        position: absolute;
        inset: 0;
        background: #fff;
      }

      .puzzle-preview-dialog iframe {
        width: 100%;
        height: 100%;
        border: 0;
        display: block;
      }

      .puzzle-preview-close {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 1;
        padding: 10px 16px;
      }
    `;
  }

  async onBeforeEnter() {
    await initialisePuzzleMetadata(this.puzzles);
  }

  async firstUpdated() {
    const response = await get<Programme[]>(`carer/${getCurrentUser().emails[0].email}/programme`);
    if (response.error) {
      return;
    }

    this.programmes = response.result;
    this.loading = false;
  }

  private async onDeleteProgramme(p: Programme, ev: Event) {
    ev.stopPropagation();

    const message = `Delete programme for ${p.participantName}?`;
    if (!window.confirm(message)) return;

    this.deletingEmail = p.participantEmail;

    const response = await _delete(`carer/${getCurrentUser().emails[0].email}/programme/${p.participantEmail}`);
    if (response.error) {
      console.error(response.error);
      return;
    }
  }

  private onRowAnimationEnd(_ev: AnimationEvent, p: Programme) {
    if (p.participantEmail !== this.deletingEmail) {
      return;
    }

    const email = p.participantEmail;
    this.deletingEmail = null;
    this.programmes = this.programmes.filter(prog => prog.participantEmail !== email);
  }

  private renderTableSkeleton() {
    const rows = Array.from({ length: 3 });
    return html`
      <div class="programmes-table-wrapper">
        <table>
          <thead>
          <tr>
            <th>Participant name</th>
            <th>Participant email</th>
            <th>Activities</th>
          </tr>
          </thead>
          <tbody>
          ${rows.map(() => html`
            <tr>
              <td>
                <div class="skeleton skeleton-text wide"></div>
              </td>
              <td>
                <div class="skeleton skeleton-text mid"></div>
              </td>
              <td>
                <div class="skeleton skeleton-pill"></div>
              </td>
            </tr>
          `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderCardsSkeleton() {
    const cards = Array.from({ length: 3 });
    return html`
      <div class="programmes-cards">
        ${cards.map(() => html`
          <div class="programme-card">
            <div>
              <div class="programme-card-header">
                <div class="skeleton skeleton-text wide"></div>
                <div class="skeleton skeleton-text mid"></div>
              </div>

              <details class="programme-card-activities" open>
                <summary>
                  <span>
                    <span class="skeleton skeleton-text short"></span>
                  </span>
                  <span class="programme-card-activities-count">
                    <span class="skeleton skeleton-text short"></span>
                  </span>
                </summary>
                <div>
                  <div class="skeleton skeleton-pill"></div>
                </div>
              </details>

              <div class="programme-card-footer">
                <div style="width: 90px; height: 28px;" class="skeleton"></div>
              </div>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private renderActivities(activities: Activity[]) {
    return html`
      <ul class="activities-list">
        ${activities.map(
          (a, index) => {
            const name =
              a.label ??
              this.puzzles.get(a.puzzle)?.find(p => p.id === a.level)?.name ??
              String(a.level);
            const previewUrl = a.options
              ? `/puzzles/${a.puzzle}?custom=${encodePresetOptions(a.options)}&label=${encodeURIComponent(a.label ?? '')}&preview=true`
              : `/puzzles/${a.puzzle}/${a.level}?preview=true`;
            return html`
              <li>
                <span class="activities-pill"
                      @click=${() => {
                        this.puzzlePreviewUrl = previewUrl;
                      }}
                      style=${a.completed ? 'background-color: green; color: white' : null}>
                  <span>${a.puzzle}</span>
                  <span>·</span>
                  <span>${name}</span>
                </span>
                ${index < activities.length - 1
              ? html`<span class="activities-arrow">↓</span>`
              : null}
              </li>
            `;
          }
        )}
      </ul>
    `;
  }

  private renderTable() {
    return html`
      <div class="programmes-table-wrapper">
        <table>
          <thead>
          <tr>
            <th>Participant name</th>
            <th>Participant email</th>
            <th></th>
            <th></th>
          </tr>
          </thead>
          <tbody>
            ${this.programmes.map(p => html`
              <tr
                data-email=${p.participantEmail}
                class=${p.participantEmail === this.deletingEmail ? 'programme-row-leave' : ''}
                @animationend=${(ev: AnimationEvent) => this.onRowAnimationEnd(ev, p)}>
                <td>
                  <a @click=${() => navigate(`/programmes/${p.participantEmail}`)}>${p.participantName}</a>
                </td>
                <td>${p.participantEmail}</td>
                <td>
                  ${this.renderActivities(p.activities)}
                </td>
                <td @click=${(ev: Event) => ev.stopPropagation()}>
                  <button
                    type="button"
                    
                    title="Delete programme"
                    @click=${(ev: Event) => this.onDeleteProgramme(p, ev)}
                    style="cursor: pointer">
                    <span></span>
                  </button>
                </td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderCards() {
    return html`
      <div class="programmes-cards">
        ${this.programmes.map(p => html`
          <div class="programme-card">
            <div>
              <div class="programme-card-header">
                <div>${p.participantName}</div>
                <div class="programme-card-email">${p.participantEmail}</div>
              </div>

              <details class="programme-card-activities">
                <summary>
                  <span>
                    <span></span>
                    <span>Activities</span>
                  </span>
                  <span class="programme-card-activities-count">
                    ${p.activities.length} item${p.activities.length === 1 ? '' : 's'}
                  </span>
                </summary>
                <div>
                  ${this.renderActivities(p.activities)}
                </div>
              </details>

              <div class="programme-card-footer">
                <button
                  
                  type="button"
                  @click=${() => navigate(`/programmes/${p.participantEmail}`)}>
                  <span></span>
                  <span>Edit</span>
                </button>
              </div>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  content() {
    return html`
      <div>
        <button 
                type="button"
                @click=${() => navigate('/')}>
          <span></span>
          <span>Back</span>
        </button>
        <h1>
          <div class="page-header">
            Programmes
            <div class="page-header-actions">
              <button
                
                type="button"
                @click=${() => navigate('/programmes/new')}
                style="padding-left: 8px; padding-right: 8px;">
                <span style="transform: translateY(-1px)"></span>
                <span>Add</span>
              </button>
            </div>
          </div></h1>

        ${this.loading
          ? html`
            ${this.renderTableSkeleton()}
            ${this.renderCardsSkeleton()}
          `
          : this.programmes.length === 0
            ? html`
              <div>
                <p>No programmes yet.</p>
                <p>Create one using the “Add” button.</p>
              </div>`
            : html`
              ${this.renderTable()}
              ${this.renderCards()}
            `}

        <div id="puzzle-preview-modal" ?hidden=${!this.puzzlePreviewUrl}>
          <div class="puzzle-preview-dialog">
            <button class="puzzle-preview-close" type="button" @click=${() => this.puzzlePreviewUrl = ''}>Close</button>

            <iframe
              src=${this.puzzlePreviewUrl}
              title="Puzzle preview"></iframe>
          </div>
        </div>
      </div>
    `;
  }
}
